// @ts-check

import { load } from '../App'
import '../css/popups.scss'
import {
  applyCSSStyleToElement,
  horizontalPositionToFlex,
  verticalPositionToFlex,
} from '../utils/dom'
import { animate, cleanUpAnimation } from '../utils/animations'
import {
  enterAnimationCatalog,
  exitAnimationCatalog,
} from './popupAnimationsCatalog'

/**
 * @typedef {(v: Record<string, any> | undefined) => void} OnCloseCallback
 */

/**
 * Specifies CSS dimension such as '10%', '10px', '10vh', 'auto'.
 * @typedef {string} Dimension
 */

/**
 * @typedef {Object} PopupOverlay
 * @property {import('../types').CSSStyle} style
 * @property {boolean=} clickThrough allow clicks behind the popup (default: true)
 * @property {boolean=} closeOnClick if true, clicks in overlay close the popup (default: true)
 */

/**
 * @typedef {Object} PopupPosition
 * @property {import('../types').HorizontalPosition} horizontal
 * @property {import('../types').VerticalPosition} vertical
 * @property {Dimension=} verticalOffset
 * @property {Dimension=} horizontalOffset
 */

/**
 * @typedef {Object} PopupOptions
 * @property {PopupPosition} position
 * @property {Dimension=} width
 * @property {Dimension=} height
 * @property {PopupOverlay=} overlay
 * @property {import('../types').CSSStyle} style
 * @property {PopupAnimation} animation
 * @property {import('../App').Loading=} loading
 */

/**
 * Embeds a dot.vu Page in an Popup fashion.
 */
class Popup {
  /** @type {import("../App").EmbedOptions} */
  _embedOptions

  /** @type {PopupOptions} */
  _options

  /** @type {HTMLDivElement} */
  _overlayElement

  /** @type {HTMLDivElement} */
  _popupElement

  /** @type {HTMLDivElement} */
  _popupAnimationContainer

  /** @type {import('../Page').Page=} */
  _page

  /** @type {OnCloseCallback=} */
  _onCloseCallback

  /** @type {'IDLE'|'LOADING'|'READY'} */
  _state

  /** @type {Promise<import('../Page').Page>} */
  _loadingPromise

  /**
   * @param {import("../App").EmbedOptions} embedOptions
   * @param {PopupOptions} options
   */
  constructor(embedOptions, options) {
    this._state = 'IDLE'
    this._options = options
    this._embedOptions = embedOptions
    this._createPopupElement()

    this._onClosePopup = (data = {}) => {
      if (!this._page) return
      if (this._onCloseCallback) this._onCloseCallback(data)
      this._unsubscribeClosePopup()
      this._hidePopup()
    }
  }

  _createPopupElement() {
    const options = this._options
    const overlay = document.createElement('div')
    const popup = document.createElement('div')
    const popupAnimationContainer = document.createElement('div')

    // Setup Overlay
    overlay.classList.add('dot-embed-popup-overlay')
    // overlay.classList.add(...popupAnimationToClasslist(options.animation))
    overlay.style.justifyContent = horizontalPositionToFlex(
      options.position.horizontal
    )
    overlay.style.alignItems = verticalPositionToFlex(options.position.vertical)
    if (options.overlay) {
      overlay.style.pointerEvents =
        options.overlay.clickThrough === true ? 'none' : 'auto'
      applyCSSStyleToElement(options.overlay.style, overlay)
    }

    if (options.overlay?.closeOnClick !== false)
      overlay.addEventListener('click', () => {
        this._hidePopup()
      })

    // Setup Popup Animation Container
    popupAnimationContainer.classList.add('dot-embed-popup-animation-container')

    // Setup Popup
    popup.classList.add('dot-embed-popup')
    popup.style.transform = `translate(${
      options.position.horizontalOffset || 0
    }, ${options.position.verticalOffset || 0})`
    if (options.width) popup.style.width = options.width
    if (options.height) popup.style.height = options.height
    applyCSSStyleToElement(options.style, popup)

    popupAnimationContainer.appendChild(popup)
    overlay.appendChild(popupAnimationContainer)
    this._overlayElement = overlay
    this._popupElement = popup
    this._popupAnimationContainer = popupAnimationContainer

    this._overlayElement.classList.add('hidden')
    document.body.appendChild(this._overlayElement)
  }

  /**
   * Loads the Dot.vu Embed and shows the popup
   * It will either show straight away (with loading) or wait for the Embed to
   * be ready (without loading).
   * @param {((page: import('../Page').Page) => any)=} cb
   */
  load(cb) {
    if (this._options.loading) this._showPopup()

    this.whenReady((page) => {
      this._subscribeClosePopup()
      if (!this._options.loading) this._showPopup()
      cb && cb(page)
    })
  }

  /**
   * Loads the Dot.vu Embed and shows the popup
   * It will either show straight away (with loading) or wait for the Embed to
   * be ready (without loading).
   * @param {((page: import('../Page').Page) => any)} cb
   */
  whenReady(cb) {
    switch (this._state) {
      case 'IDLE':
        this.preload().then(cb)
        break
      case 'LOADING':
        this._loadingPromise.then(cb)
        break
      case 'READY':
        if (this._page) cb(this._page)
        break
      default:
        throw new Error(`State "${this._state}" is not recognized.`)
    }
  }

  /**
   *
   * @param {OnCloseCallback} cb
   */
  onClose(cb) {
    this._onCloseCallback = cb
  }

  /**
   * Loads the Dot.vu Embed
   * @param {((page: import('../Page').Page) => any)=} cb
   */
  preload(cb) {
    this._state = 'LOADING'
    this._loadingPromise = new Promise((resolve, reject) => {
      this._popupElement.innerHTML = ''
      load(
        this._popupElement,
        Object.assign({}, this._embedOptions, {
          width: '100%',
          height: this._options.height || 'auto',
          loading: this._options.loading,
        }),
        (page) => {
          this._page = page
          this._state = 'READY'
          resolve(page)
          cb && cb(page)
        }
      )
    })
    return this._loadingPromise
  }

  _subscribeClosePopup() {
    if (!this._page) return
    this._page.events.subscribe('__dot_embed_close_popup__', this._onClosePopup)
  }

  _unsubscribeClosePopup() {
    if (!this._page) return
    this._page.events.unsubscribe(
      '__dot_embed_close_popup__',
      this._onClosePopup
    )
  }

  _showPopup() {
    requestAnimationFrame(() => {
      showPopup(this, this._options.animation)
      this._overlayElement.classList.remove('hidden')
    })
  }

  _hidePopup() {
    return hidePopup(this, this._options.animation).then(() => {
      this._overlayElement.classList.add('hidden')
    })
  }

  unload() {
    this._state = 'IDLE'
    this._unsubscribeClosePopup()
    document.body.removeChild(this._overlayElement)
  }
}

/**
 * @typedef {Object} PopupAnimationObj
 * @property {import('../utils/animations').GenericAnimationObject} overlay
 * @property {import('../utils/animations').GenericAnimationObject} popup
 */

/**
 * @typedef {Object} PopupEnterAnimationObj
 * @property {import('../utils/animations').EnterAnimationObject} overlay
 * @property {import('../utils/animations').EnterAnimationObject} popup
 */

/**
 * @typedef {Object} PopupExitAnimationObj
 * @property {import('../utils/animations').ExitAnimationObject} overlay
 * @property {import('../utils/animations').ExitAnimationObject} popup
 */

/**
 * @typedef {Object} PopupFullAnimationObj
 * @property {PopupEnterAnimationObj} onEnter
 * @property {PopupExitAnimationObj} onExit
 */

/**
 * @typedef {(
 *  'appear' |
 *  'fade-in' |
 *  'slide-in-top' |
 *  'slide-in-bottom' |
 *  'slide-in-left' |
 *  'slide-in-right' |
 *  'ease-in-top' |
 *  'ease-in-bottom' |
 *  'ease-in-left' |
 *  'ease-in-right'
 * )} PopupEnterAnimationSimple
 */

/**
 * @typedef {(
 *  'disappear' |
 *  'fade-out' |
 *  'slide-out-top' |
 *  'slide-out-bottom' |
 *  'slide-out-left' |
 *  'slide-out-right' |
 *  'ease-out-top' |
 *  'ease-out-bottom' |
 *  'ease-out-left' |
 *  'ease-out-right'
 * )} PopupExitAnimationSimple
 */

/**
 * @typedef {Object} PopupAnimation
 * @property {PopupEnterAnimationSimple | PopupEnterAnimationObj} onEnter
 * @property {PopupExitAnimationSimple | PopupExitAnimationObj} onExit
 */

/**
 * @param {PopupAnimation} animation
 * @returns {PopupAnimationObj}
 */
function getPopupEnterAnimationObject({ onEnter }) {
  return typeof onEnter === 'string' ? enterAnimationCatalog[onEnter] : onEnter
}

/**
 * @param {PopupAnimation} animation
 */
function getPopupExitAnimationObject({ onExit }) {
  return typeof onExit === 'string' ? exitAnimationCatalog[onExit] : onExit
}

/**
 *
 * @param {Popup} popup
 * @param {PopupAnimationObj} animation
 * @returns {Promise<void>}
 */
function animatePopup(popup, animation) {
  return Promise.all([
    animate(popup._overlayElement, animation.overlay),
    animate(popup._popupAnimationContainer, animation.popup),
  ]).then(() => {
    cleanUpAnimation(popup._popupAnimationContainer, animation.popup)
    cleanUpAnimation(popup._overlayElement, animation.overlay)
  })
}

/**
 *
 * @param {Popup} popup
 * @param {PopupAnimation} animation
 * @returns {Promise<void>}
 */
function showPopup(popup, animation) {
  return animatePopup(popup, getPopupEnterAnimationObject(animation))
}

/**
 *
 * @param {Popup} popup
 * @param {PopupAnimation} animation
 * @returns {Promise<void>}
 */
function hidePopup(popup, animation) {
  return animatePopup(popup, getPopupExitAnimationObject(animation))
}
export { Popup }
